import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Loader2, Headset, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";

export default function Support() {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/thread");
      setMessages(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/support/message", { text: msg });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/support/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, data]);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Headset size={26} className="text-cyan-300" />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold gold-text">{t("support")}</h1>
          <p className="text-xs text-slate-400">{t("supportDesc")}</p>
        </div>
      </div>

      <div className="mw-card p-4 flex flex-col h-[62vh]" data-testid="support-chat">
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {loading ? (
            <div className="h-full grid place-items-center text-amber-400"><Loader2 className="animate-spin" /></div>
          ) : messages.length === 0 ? (
            <div className="h-full grid place-items-center text-center text-slate-500 text-sm px-6">
              <div>
                <Headset size={32} className="mx-auto mb-2 text-slate-600" />
                {t("supportEmpty")}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.sender}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${
                  m.sender === "user"
                    ? "bg-amber-500/90 text-black rounded-br-sm"
                    : "border border-cyan-400/30 bg-cyan-500/10 text-cyan-50 rounded-bl-sm"
                }`}>
                  {m.sender === "admin" && <p className="text-[10px] font-bold text-cyan-300 mb-0.5">{t("supportTeam")}</p>}
                  {m.image_url && <img src={m.image_url} alt="image" className="mb-1 max-h-48 min-h-[60px] min-w-[80px] rounded-lg bg-black/20 object-contain" data-testid="support-image" />}
                  {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                  <p className="text-[10px] opacity-60 mt-1 text-right">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>

        <div className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="support-image-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="support-image-btn" title={t("sendImage")}
            className="grid place-items-center h-11 w-11 rounded-xl border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 shrink-0 hover:border-cyan-400 transition-colors">
            <ImagePlus size={18} />
          </button>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t("typeMessage")}
            data-testid="support-input"
            className="flex-1 rounded-xl border border-amber-500/30 bg-black/40 px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500/80"
          />
          <button onClick={send} disabled={busy} data-testid="support-send-btn"
            className="grid place-items-center h-11 w-11 rounded-xl btn-gold shrink-0">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
